import {
   DosageBreak,
   DosagePeriod,
   DosageStageUnion,
   DosageTimeOfDay,
   DosageTimeUnit,
   FrequencyDosageCycle,
   TimeOfDayPortion,
   TimesOfDayDosageCycle,
} from '@dreryk/egabinet-prescription-published-language-api';

import { isFrequencyDosageCycle, isTimesOfDayDosageCycle } from './extendedDosage';
import { getP1DosagePortion } from './preciseDosagePortion';

export const DOSAGE_TIME_UNITS: DosageTimeUnit[] = ['HOUR', 'DAY', 'WEEK', 'MONTH'];

// P1 has no duration in hours - hours are allowed only for the interval between doses
export const DOSAGE_DURATION_UNITS: DosageTimeUnit[] = ['DAY', 'WEEK', 'MONTH'];

export const getLockedDurationUnit = (intervalUnit: DosageTimeUnit | null | undefined): DosageTimeUnit | undefined =>
   intervalUnit && intervalUnit !== 'HOUR' ? intervalUnit : undefined;

export const TIMES_OF_DAY_DURATION_UNIT: DosageTimeUnit = 'DAY';

export const DOSAGE_TIMES_OF_DAY: DosageTimeOfDay[] = ['MORNING', 'NOON', 'AFTERNOON', 'EVENING'];

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const ADMINISTRATION_INTERVAL_DAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export type AdministrationIntervalDays = typeof ADMINISTRATION_INTERVAL_DAYS[number];

export type DosageCycleClass = 'FrequencyDosageCycle' | 'TimesOfDayDosageCycle';

export const getAdministrationIntervalDays = (value: number | undefined): AdministrationIntervalDays =>
   ADMINISTRATION_INTERVAL_DAYS.find((days) => days === value) ?? ADMINISTRATION_INTERVAL_DAYS[0];

const createEmptyTimeOfDayPortions = (): TimeOfDayPortion[] =>
   DOSAGE_TIMES_OF_DAY.map((timeOfDay) => ({ timeOfDay: timeOfDay, portion: 0 }));

export const createFrequencyDosageCycle = (): FrequencyDosageCycle => ({
   class: 'FrequencyDosageCycle',
   portion: 1,
   interval: { amount: 8, unit: 'HOUR' },
   duration: { amount: 2, unit: 'WEEK' },
});

export const createTimesOfDayDosageCycle = (): TimesOfDayDosageCycle => ({
   class: 'TimesOfDayDosageCycle',
   portions: createEmptyTimeOfDayPortions(),
   administrationIntervalDays: 1,
   duration: { amount: 14, unit: TIMES_OF_DAY_DURATION_UNIT },
});

export const createDosageBreak = (): DosageBreak => ({
   class: 'DosageBreak',
   duration: { amount: 1, unit: 'WEEK' },
});

export const duplicateStage = (stage: DosageStageUnion): DosageStageUnion => {
   if (isFrequencyDosageCycle(stage)) {
      return {
         class: 'FrequencyDosageCycle',
         portion: stage.portion,
         interval: { ...stage.interval },
         duration: { ...stage.duration },
      };
   }

   if (isTimesOfDayDosageCycle(stage)) {
      return {
         class: 'TimesOfDayDosageCycle',
         portions: stage.portions.map((portion) => ({ ...portion })),
         administrationIntervalDays: stage.administrationIntervalDays,
         duration: { ...stage.duration },
      };
   }

   return {
      class: 'DosageBreak',
      duration: { ...stage.duration },
   };
};

export const getTimesOfDaySchema = (cycle: TimesOfDayDosageCycle, formatPortion: (portion: number) => string): string =>
   DOSAGE_TIMES_OF_DAY.map((timeOfDay) =>
      formatPortion(cycle.portions.find((portion) => portion.timeOfDay === timeOfDay)?.portion ?? 0)
   ).join('-');

const withAllTimeOfDayPortionsByIndex = (portions: Partial<TimeOfDayPortion>[]): TimeOfDayPortion[] =>
   DOSAGE_TIMES_OF_DAY.map((timeOfDay, index) => ({
      timeOfDay: timeOfDay,
      portion: Number(portions[index]?.portion ?? 0),
   }));

const withAllTimeOfDayPortionsByTimeOfDay = (portions: Partial<TimeOfDayPortion>[]): TimeOfDayPortion[] =>
   DOSAGE_TIMES_OF_DAY.map((timeOfDay) => ({
      timeOfDay: timeOfDay,
      portion: Number(portions.find((portion) => portion?.timeOfDay === timeOfDay)?.portion ?? 0),
   }));

export const getStageWithTimesOfDay = (stage: DosageStageUnion): DosageStageUnion => {
   if (!isTimesOfDayDosageCycle(stage)) {
      return stage;
   }

   return {
      ...stage,
      portions: withAllTimeOfDayPortionsByIndex(stage.portions ?? []),
   };
};

const normalizePeriod = (period: DosagePeriod): DosagePeriod => ({
   amount: Number(period.amount),
   unit: period.unit,
});

export const normalizeStageForForm = (stage: DosageStageUnion): DosageStageUnion => {
   if (isTimesOfDayDosageCycle(stage)) {
      return {
         class: 'TimesOfDayDosageCycle',
         portions: withAllTimeOfDayPortionsByTimeOfDay(stage.portions ?? []),
         administrationIntervalDays: Number(stage.administrationIntervalDays ?? 1),
         duration: normalizePeriod(stage.duration),
      };
   }

   return stage;
};

export const normalizeStageForSave = (stage: DosageStageUnion): DosageStageUnion => {
   if (isFrequencyDosageCycle(stage)) {
      return {
         class: 'FrequencyDosageCycle',
         portion: getP1DosagePortion(Number(stage.portion)),
         interval: normalizePeriod(stage.interval),
         duration: normalizePeriod(stage.duration),
      };
   }

   if (isTimesOfDayDosageCycle(stage)) {
      return {
         class: 'TimesOfDayDosageCycle',
         portions: withAllTimeOfDayPortionsByIndex(stage.portions ?? [])
            .filter((portion) => portion.portion > 0)
            .map((portion) => ({ ...portion, portion: getP1DosagePortion(portion.portion) })),
         administrationIntervalDays: Number(stage.administrationIntervalDays ?? 1),
         duration: normalizePeriod(stage.duration),
      };
   }

   return {
      class: 'DosageBreak',
      duration: normalizePeriod(stage.duration),
   };
};
